/**
 * FB-1429 — corrected dialog / focus-management primitive.
 *
 * Re-registers the Eurus `xModal` and `xFocusElement` Alpine stores defined in
 * `assets/theme.js` (~L1505-1581).
 *
 * Loading after theme.js is NOT sufficient to win. theme.js registers its
 * `alpine:init` listener from inside a `requestAnimationFrame` callback
 * (assets/theme.js:1373), which runs after this file has parsed — so its
 * handler is queued second and overwrites ours. See the note above the event
 * bindings at the bottom of this file for how that is handled.
 *
 * Why re-register rather than edit theme.js: theme.js is vendor Eurus code and
 * edits are lost on theme upgrade. Every overlay in the theme already calls
 * `$store.xModal.focus(...)` / `.removeFocus()`, so correcting the store fixes
 * all of them without touching ~21 call sites.
 *
 * Defects in the original implementation this replaces:
 *
 *   1. `if (window.innerWidth < 1025) return;` guarded both trapFocus and
 *      removeTrapFocus — there was NO focus management at all below 1025px.
 *   2. removeTrapFocus passed freshly-constructed arrow functions to
 *      removeEventListener, which matches on identity — the focusin/focusout
 *      handlers were never removed and accumulated on document for the life of
 *      the page, holding stale `first`/`last` references.
 *   3. The focusable query had no visibility filter, so `display:none`
 *      elements, collapsed-`<details>` descendants and hidden panels were
 *      treated as trap boundaries and received focus.
 *   4. The focusable set was captured once at open time, so dialogs whose DOM
 *      changes while open (cart lines, search results) trapped against stale
 *      boundaries.
 *
 * Backward compatibility — these signatures are load-bearing and preserved:
 *
 *   $store.xModal.activeElement            (string; read by header-search-block.liquid)
 *   $store.xModal.focused                  (bool)
 *   $store.xModal.setActiveElement(id)
 *   $store.xModal.focus(containerId, focusElementId)
 *   $store.xModal.removeFocus()            (restores focus to activeElement)
 *   xFocusElement.trapFocus(containerId, focusElementId)   — string ids
 *   xFocusElement.removeTrapFocus(element)                 — an Element, not an id
 *
 * Dialog semantics (role/aria-modal/accessible name/background inert/Escape)
 * are OPT-IN per container via the `data-a11y-dialog` attribute. They are not
 * applied to every caller, because `focus()` is shared by ~18 surfaces outside
 * this ticket's scope and silently turning several of them into modal dialogs
 * would be an unreviewed behavior change. Each overlay subtask opts its own
 * panel in.
 */
(() => {
  'use strict';

  /**
   * Elements that can hold focus. Deliberately broader than the original
   * single-string selector so that summary/audio/video/contenteditable are
   * accounted for; visibility is filtered separately in isFocusable().
   */
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button',
    'input',
    'select',
    'textarea',
    'summary',
    'iframe',
    'object',
    'embed',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]'
  ].join(', ');

  /** Resolve a string id or an Element into an Element. */
  const resolveEl = (ref) => {
    if (!ref) return null;
    if (ref instanceof Element) return ref;
    return document.getElementById(String(ref));
  };

  /**
   * True when the element can actually receive focus right now.
   *
   * getClientRects() is the reliable "is rendered" test: it returns an empty
   * list for display:none, for descendants of display:none, and for content
   * inside a closed <details> — which is exactly audit findings 81218/81220
   * (Swiper pagination bullets inside a collapsed picker) and 81201/81203.
   *
   * Note it does NOT catch elements that are rendered but visually moved
   * offscreen (e.g. a closed drawer at -translate-x-full, finding 81224) or
   * hover-revealed controls at opacity:0 (finding 81226). Those are page-level
   * tab-order problems fixed by marking the hidden container inert, not by
   * filtering inside a trap.
   */
  const isFocusable = (el) => {
    if (!el || !el.isConnected) return false;
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;

    const tabindex = el.getAttribute('tabindex');
    if (tabindex !== null && parseInt(tabindex, 10) < 0) return false;

    // Inside an inert or aria-hidden subtree.
    if (el.closest('[inert]')) return false;
    if (el.closest('[aria-hidden="true"]')) return false;

    // Not rendered at all.
    if (!el.getClientRects().length) return false;

    const style = window.getComputedStyle(el);
    if (style.visibility === 'hidden' || style.visibility === 'collapse') return false;

    return true;
  };

  /**
   * True when the element is still rendered. Used to detect an overlay that
   * was dismissed by a path which never called removeFocus() — clicking a
   * backdrop, an Alpine `show = false` with no store call — so the trap can
   * release itself instead of stranding the page.
   */
  const isRendered = (el) => {
    if (!el || !el.isConnected) return false;
    if (!el.getClientRects().length) return false;
    const style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && style.visibility !== 'collapse';
  };

  /** Visible, focusable descendants of `container`, in DOM order. */
  const focusableWithin = (container) => {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isFocusable);
  };

  /**
   * Mark everything outside `element` — but inside `root` — as inert, so
   * neither focus nor assistive tech can reach content the open surface covers
   * (WCAG 2.4.3 / the reading-order-browse-outside-modal findings).
   *
   * Walks element -> root marking siblings at each level. aria-hidden is set
   * alongside inert as a fallback for Safari < 15.5, which does not support
   * inert. Records exactly which attributes we added so unmarking never strips
   * an aria-hidden that belonged to the page.
   *
   * `root` defaults to document.body, which is the dialog case: everything on
   * the page outside the dialog. FB-1430's mobile-nav level scoping passes the
   * drawer root instead, to seal off the levels *behind* the open one without
   * disturbing the page-level sweep this same trap already applied.
   *
   * @param {Element} element the subtree to keep reachable
   * @param {Element} root    outermost ancestor to sweep up to (inclusive)
   * @returns {Array<{el: Element, addedInert: boolean, addedAriaHidden: boolean}>}
   */
  const markInertOutside = (element, root = document.body) => {
    const marked = [];
    let node = element;

    while (node && node !== root && node.parentElement) {
      const parent = node.parentElement;

      for (const sibling of Array.from(parent.children)) {
        if (sibling === node) continue;
        if (/^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/.test(sibling.tagName)) continue;

        // Opt-out for click-catching backdrops. `inert` blocks pointer events
        // as well as focus, so marking a backdrop would silently break
        // click-outside-to-close. Backdrops hold no content, so leaving them
        // out of the sweep costs nothing for assistive tech.
        if (sibling.hasAttribute('data-a11y-dialog-backdrop')) continue;

        const addedInert = !sibling.hasAttribute('inert');
        const addedAriaHidden = sibling.getAttribute('aria-hidden') !== 'true';

        if (addedInert) sibling.setAttribute('inert', '');
        if (addedAriaHidden) sibling.setAttribute('aria-hidden', 'true');
        if (addedInert || addedAriaHidden) marked.push({ el: sibling, addedInert, addedAriaHidden });
      }

      if (parent === root) break;
      node = parent;
    }

    return marked;
  };

  const unmarkInert = (marked) => {
    for (const { el, addedInert, addedAriaHidden } of marked) {
      if (addedInert) el.removeAttribute('inert');
      if (addedAriaHidden) el.removeAttribute('aria-hidden');
    }
  };

  /**
   * Give an opted-in dialog container its semantics and accessible name.
   * Prefers aria-labelledby against a visible heading; falls back to an
   * existing aria-label, then to the data-a11y-dialog attribute value.
   * Returns the attributes we added so they can be reverted on close.
   */
  const applyDialogSemantics = (container) => {
    const added = [];
    const addAttr = (name, value) => {
      if (container.hasAttribute(name)) return;
      container.setAttribute(name, value);
      added.push(name);
    };

    addAttr('role', 'dialog');
    addAttr('aria-modal', 'true');

    if (!container.hasAttribute('aria-labelledby') && !container.hasAttribute('aria-label')) {
      const heading = container.querySelector('h1, h2, h3, h4, h5, h6, [role="heading"]');

      // A visually-hidden heading is still a valid label target, so this
      // checks for text content rather than for visibility.
      if (heading && heading.textContent.trim()) {
        if (!heading.id) heading.id = `a11y-dialog-title-${Math.random().toString(36).slice(2, 9)}`;
        addAttr('aria-labelledby', heading.id);
      } else {
        const label = container.getAttribute('data-a11y-dialog');
        if (label) addAttr('aria-label', label);
      }
    }

    return added;
  };

  const revertDialogSemantics = (container, added) => {
    for (const name of added) container.removeAttribute(name);
  };

  /**
   * The single active trap. The original store also supported only one trap at
   * a time; keeping that constraint avoids changing behavior for the nested
   * cases (e.g. size chart opened from a PDP popup) beyond fixing teardown.
   */
  let active = null;

  /**
   * Pending rAF handle for a deferred trapFocus. Cancelled by removeTrapFocus
   * so an overlay that opens and closes within the same frame cannot engage a
   * trap after it has already been torn down.
   */
  let pendingEngage = null;

  /**
   * Bumped by every explicit trapFocus / removeTrapFocus. trapFocusWhenReady
   * polls across many frames, so cancelling an rAF handle is not enough to stop
   * it — the handle only exists between frames. The generation check is what
   * actually stops a poll that a later, deliberate focus call has superseded.
   */
  let engageGeneration = 0;

  /**
   * Latest poll token per container, so starting a second trapFocusWhenReady on
   * the *same* container replaces the first without disturbing polls on other
   * containers.
   *
   * Per-container rather than one global token, because two panels legitimately
   * poll at once: the "Choose options" flyout opens its per-card panel and the
   * shared mobile popup from the same click, and exactly one of them will turn
   * out to be the one rendered at the current viewport. A global cancel would
   * let whichever started second kill the one that was going to win.
   */
  const readyTokens = new WeakMap();
  let readySequence = 0;

  const cancelPendingEngage = () => {
    engageGeneration++;
    if (pendingEngage === null) return;
    cancelAnimationFrame(pendingEngage);
    pendingEngage = null;
  };

  const releaseActive = () => {
    if (!active) return;

    document.removeEventListener('keydown', active.onKeydown, true);
    document.removeEventListener('focusin', active.onFocusin, true);
    if (active.observer) active.observer.disconnect();

    if (active.marked) unmarkInert(active.marked);
    if (active.addedSemantics) revertDialogSemantics(active.container, active.addedSemantics);
    if (active.addedContainerTabindex) active.container.removeAttribute('tabindex');

    active = null;
  };

  const engage = (container, elementFocus) => {
    releaseActive();

    const isDialog = container.hasAttribute('data-a11y-dialog');

    const state = {
      container,
      isDialog,
      marked: null,
      addedSemantics: null,
      addedContainerTabindex: false
    };

    /**
     * Wrap Tab at the boundaries. The focusable set is re-queried on every
     * keystroke rather than captured at open time, because dialog contents are
     * dynamic (cart lines, live search results, expanding <details>).
     */
    state.onKeydown = (event) => {
      if (!isRendered(container)) {
        releaseActive();
        return;
      }

      // Escape always releases the trap, and additionally asks an opted-in
      // dialog to close. Releasing unconditionally is a safety valve: an
      // overlay whose close path forgets removeFocus() must never leave a
      // keyboard user with no way out (Accessibility Constitution §13).
      if (event.key === 'Escape') {
        if (isDialog) container.dispatchEvent(new CustomEvent('a11y-dialog:close', { bubbles: true }));
        releaseActive();
        return;
      }

      if (event.key !== 'Tab') return;

      const elements = focusableWithin(container);
      if (!elements.length) {
        event.preventDefault();
        return;
      }

      const first = elements[0];
      const last = elements[elements.length - 1];
      const target = event.target;

      if (event.shiftKey && (target === first || target === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && target === last) {
        event.preventDefault();
        first.focus();
      }
    };

    /**
     * Catch every other way focus can leave — click, programmatic focus(),
     * browser chrome round-trip — and pull it back to the dialog. This is what
     * the original focusin handler was trying to do before its listeners
     * leaked.
     */
    state.onFocusin = (event) => {
      if (!isRendered(container)) {
        releaseActive();
        return;
      }
      if (container.contains(event.target)) return;
      if (event.target === document.body) return;

      const elements = focusableWithin(container);
      if (elements.length) {
        elements[0].focus();
      } else {
        container.focus();
      }
    };

    if (isDialog) {
      state.addedSemantics = applyDialogSemantics(container);
      state.marked = markInertOutside(container);
    }

    document.addEventListener('keydown', state.onKeydown, true);
    document.addEventListener('focusin', state.onFocusin, true);

    /**
     * Release without waiting for interaction when the overlay is hidden by a
     * path that never called removeFocus(). This matters most for opted-in
     * dialogs: `inert` blocks pointer events as well as focus, so a leaked
     * trap would leave the whole page unclickable with no event to recover on.
     * Alpine's x-show toggles inline style, so watching attributes on the
     * container and its ancestors is sufficient.
     */
    state.observer = new MutationObserver(() => {
      if (!isRendered(container)) releaseActive();
    });

    for (let node = container; node && node !== document.body; node = node.parentElement) {
      state.observer.observe(node, {
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'inert']
      });
    }

    active = state;

    // Move focus in: the requested element, else the first visible control,
    // else the container itself.
    const requested = resolveEl(elementFocus);

    if (requested && isFocusable(requested)) {
      requested.focus();
      return;
    }

    const elements = focusableWithin(container);
    if (elements.length) {
      elements[0].focus();
      return;
    }

    if (!container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '-1');
      state.addedContainerTabindex = true;
    }
    container.focus();
  };

  const registerStores = () => {
    window.Alpine.store('xFocusElement', {
      // Retained for backward compatibility; the trap uses FOCUSABLE_SELECTOR.
      focusableElements: [FOCUSABLE_SELECTOR],
      listeners: {},

      /**
       * @param {string|Element} container    dialog container (id or element)
       * @param {string|Element} elementFocus element to focus on open
       */
      trapFocus(container, elementFocus) {
        const c = resolveEl(container);
        if (!c) return;

        // Defer one frame: several call sites fire from x-intersect.full or a
        // setTimeout(0) immediately after the panel becomes visible, and the
        // panel may not have laid out yet — which would make every child fail
        // the getClientRects() visibility test.
        cancelPendingEngage();
        pendingEngage = requestAnimationFrame(() => {
          pendingEngage = null;
          if (!c.isConnected) return;
          engage(c, elementFocus);
        });
      },

      /**
       * Move focus to the first focusable element inside `container` as soon as
       * one exists, without trapping focus or hiding the background.
       *
       * Added in FB-1432 for the card "Choose options" flyout, whose content is
       * injected by an ajax fetch (`loadChooseOptions` in assets/quick-view.js)
       * that can still be in flight when the panel is revealed. A plain
       * `$nextTick(() => panel.querySelector(...).focus())` therefore finds an
       * empty container and silently does nothing — so this polls by frame until
       * the markup lands.
       *
       * Deliberately NOT `trapFocus`: that engages the trap, and the Escape
       * handling that lets a user out of a trap is gated behind the
       * `data-a11y-dialog` opt-in. A caller that wants the full modal treatment
       * should opt in and call `trapFocus` instead. This is the lighter contract
       * — focus goes in, Tab continues out of the panel normally.
       *
       * @param {string|Element} container
       * @param {number} timeout ms to keep waiting for content before giving up
       * @returns {void}
       */
      focusWhenReady(container, timeout = 2000) {
        const c = resolveEl(container);
        if (!c) return;

        const started = performance.now();
        const attempt = () => {
          // Abort if the panel closed again while we were waiting; focusing a
          // hidden element is the defect this ticket exists to fix.
          if (!c.isConnected || !isRendered(c)) return;

          const [first] = focusableWithin(c);
          if (first) {
            first.focus();
            return;
          }

          if (performance.now() - started < timeout) requestAnimationFrame(attempt);
        };

        requestAnimationFrame(attempt);
      },

      /**
       * Engage the full modal treatment — trap, dialog semantics, inert
       * background, Escape — as soon as the container is on screen AND has
       * something focusable in it.
       *
       * Added in FB-1435 for the card "Choose options" flyout, which needs both
       * halves of the problem solved at once and could not use either existing
       * method on its own:
       *
       *   - `trapFocus` engages immediately. The flyout's contents arrive over
       *     ajax (`loadChooseOptions` in assets/quick-view.js), so an immediate
       *     engage traps an empty box and falls back to focusing the container.
       *   - `focusWhenReady` waits for content but deliberately does not trap.
       *
       * The `isRendered` gate is load-bearing, not defensive. The flyout's
       * `showOptions` flag is toggled at every viewport width, but the desktop
       * panel carries `hidden md:block` and the mobile popup carries
       * `md:hidden` — so at any given width one of the two is `display: none`
       * while its open flag is true. Engaging on the hidden one would apply
       * `role="dialog"` and mark the entire page `inert` behind a panel nobody
       * can see, and because `inert` blocks pointer events the page would be
       * dead with no visible cause. Waiting for the container to render is what
       * routes the trap to whichever panel is actually the one on screen.
       *
       * Both panels therefore poll concurrently on every open, and the loser
       * simply times out — which is why the poll token is per container (see
       * `readyTokens`) and this does not call `cancelPendingEngage`.
       *
       * Gives up silently after `timeout`: a panel whose ajax never lands is
       * better left untrapped than trapped empty.
       *
       * @param {string|Element} container
       * @param {string|Element|null} elementFocus element to focus on engage;
       *   falls back to the first focusable child. Pass the currently focused
       *   element to re-engage a trap without moving focus.
       * @param {number} timeout ms to keep waiting for content before giving up
       */
      trapFocusWhenReady(container, elementFocus = null, timeout = 2000) {
        const c = resolveEl(container);
        if (!c) return;

        const token = ++readySequence;
        readyTokens.set(c, token);

        const generation = engageGeneration;
        const started = performance.now();

        const attempt = () => {
          // A newer poll owns this container; it will do the work.
          if (readyTokens.get(c) !== token) return;

          // An explicit trapFocus / removeTrapFocus has happened since.
          if (generation !== engageGeneration || !c.isConnected) {
            readyTokens.delete(c);
            return;
          }

          if (isRendered(c) && focusableWithin(c).length) {
            readyTokens.delete(c);
            engage(c, elementFocus);
            return;
          }

          if (performance.now() - started < timeout) {
            requestAnimationFrame(attempt);
            return;
          }

          readyTokens.delete(c);
        };

        requestAnimationFrame(attempt);
      },

      /**
       * @param {Element|string|null} elementToFocus element to restore focus to
       */
      removeTrapFocus(elementToFocus = null) {
        cancelPendingEngage();
        releaseActive();

        const target = resolveEl(elementToFocus);
        if (target && isFocusable(target)) target.focus();
      }
    });

    window.Alpine.store('xModal', {
      activeElement: '',
      focused: false,

      setActiveElement(element) {
        this.activeElement = element;
      },

      focus(container, elementFocus) {
        this.focused = true;
        window.Alpine.store('xFocusElement').trapFocus(container, elementFocus);
      },

      /**
       * `elementId` is accepted because snippets/media-gallery.liquid calls
       * removeFocus('postion-image-...'). The original signature took no
       * argument and silently ignored it, so that call site restored focus to
       * whatever activeElement happened to hold — usually nothing. Honouring
       * the argument matches the call site's evident intent; it falls back to
       * activeElement for every other caller.
       */
      removeFocus(elementId = null) {
        this.focused = false;
        const openedBy = resolveEl(elementId) || resolveEl(this.activeElement);
        window.Alpine.store('xFocusElement').removeTrapFocus(openedBy);
      }
    });
  };

  /**
   * Registered on BOTH events, deliberately.
   *
   * theme.js does not register its `alpine:init` listener at parse time — it
   * wraps the registration in `requestAnimationFrame` (assets/theme.js:1373).
   * That callback runs after this file has parsed, so theme.js's listener is
   * queued *after* ours even though its <script> tag comes first. Handlers
   * fire in registration order, so on `alpine:init` theme.js runs last and its
   * store definitions overwrite ours.
   *
   * `alpine:initialized` fires once, after every `alpine:init` handler has
   * run, which makes this file authoritative regardless of who registered
   * when. The `alpine:init` pass is kept so the corrected stores exist for any
   * component that touches them during initialization.
   *
   * Do not "simplify" this to a single alpine:init listener — that is the bug
   * this comment exists to prevent.
   */
  const registerPreservingState = () => {
    const previous = window.Alpine.store('xModal');
    registerStores();

    // Carry over opener state if anything recorded one before we took over.
    if (previous && previous.activeElement) {
      window.Alpine.store('xModal').activeElement = previous.activeElement;
    }
  };

  document.addEventListener('alpine:init', registerPreservingState);
  document.addEventListener('alpine:initialized', registerPreservingState);

  /**
   * Minimal shared surface for other custom a11y modules.
   *
   * Only the inert sweep and the two visibility predicates are published, and
   * only because other consumers need exactly these semantics:
   * `assets/custom-a11y-mobile-nav.js` seals off the mobile-nav levels behind
   * the open one, and `assets/custom-a11y-choose-options.js` has to tell an
   * open flyout from a closed one the same way the trap does. Duplicating
   * either would mean two implementations drifting apart — the inert walk has
   * "which attributes did we add" bookkeeping that must stay in sync, and
   * `isRendered` encodes the getClientRects-plus-visibility test that the whole
   * primitive's notion of "on screen" rests on.
   *
   * The trap itself is deliberately NOT published — it is reached through the
   * `$store.xFocusElement` / `$store.xModal` stores, which is the contract the
   * ~21 existing call sites already use.
   */
  window.FarmacyA11y = Object.assign(window.FarmacyA11y || {}, {
    markInertOutside,
    unmarkInert,
    isFocusable,
    isRendered
  });
})();
